import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

/**
 * Custom hook to fetch drug pricing form data based on persona
 * @param {string} formId - The drug pricing form ID
 * @param {string} persona - The user persona ('customer' or 'admin'/'super_admin')
 * @param {boolean} enabled - Whether to fetch data or not
 * @returns {object} - { data, loading, error, refetch }
 */
export const useDrugPricingFormData = (formId, persona = 'customer', enabled = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDrugPricingForm = useCallback(async () => {
    if (!formId || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      // Determine endpoint based on persona
      const endpoint = persona === 'admin' || persona === 'super_admin' 
        ? `/api/drug-pricing-forms/admin/${formId}`
        : `/api/drug-pricing-forms/${formId}`;

      const response = await axiosInstance.get(endpoint);
      
      if (response.data && response.data.data) {
        setData(response.data.data);
      } else {
        setData(response.data);
      }
    } catch (err) {
      console.error('Error fetching drug pricing form:', err);
      setError(err.response?.data?.message || 'Failed to fetch drug pricing form');
    } finally {
      setLoading(false);
    }
  }, [formId, persona, enabled]);

  useEffect(() => {
    fetchDrugPricingForm();
  }, [fetchDrugPricingForm]);

  const refetch = () => {
    fetchDrugPricingForm();
  };

  return {
    data,
    loading,
    error,
    refetch
  };
};

export default useDrugPricingFormData;